import { useEffect, useRef, useCallback, memo } from "react";
import { ProductListGrid } from "./ProductListGrid";
import { ProductListTable } from "./ProductListTable";
import { Loader2 } from "lucide-react";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

interface VirtualProductListProps {
  products: Product[];
  viewMode: 'grid' | 'table';
  hasMore: boolean;
  isFetchingNextPage: boolean;
  loadMore: () => void;
  onProductClick?: (product: Product) => void;
  onEditProduct: (product: Product) => void;
  onDeleteProduct: (product: Product) => void;
  onCreateCredit?: (product: Product) => void;
  onImageClick?: (imageUrl: string, e: React.MouseEvent) => void;
}

const VirtualProductListComponent = ({
  products,
  viewMode,
  hasMore,
  isFetchingNextPage,
  loadMore,
  onProductClick,
  onEditProduct,
  onDeleteProduct,
  onCreateCredit,
  onImageClick
}: VirtualProductListProps) => {
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const lastProductRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for infinite scroll
  const observerCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !isFetchingNextPage) {
      console.log('🔄 Loading more products...');
      loadMore();
    }
  }, [hasMore, isFetchingNextPage, loadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '100px', // Start loading 100px before reaching the end
      threshold: 0.1
    });

    const currentLastProduct = lastProductRef.current;
    if (currentLastProduct) {
      observer.observe(currentLastProduct);
    }

    return () => {
      if (currentLastProduct) {
        observer.unobserve(currentLastProduct);
      }
    };
  }, [observerCallback, products.length]);

  if (products.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-lg font-medium mb-2">No products found</div>
        <div className="text-sm">Add products to this order to get started.</div>
      </div>
    );
  }

  return (
    <div ref={scrollElementRef} className="space-y-4">
      {viewMode === 'grid' ? (
        <ProductListGrid
          products={products}
          onProductClick={onProductClick}
          onEditProduct={onEditProduct}
          onDeleteProduct={onDeleteProduct}
          onImageClick={onImageClick}
        />
      ) : (
        <ProductListTable
          products={products}
          onProductClick={onProductClick}
          onEditProduct={onEditProduct}
          onDeleteProduct={onDeleteProduct}
          onImageClick={onImageClick}
        />
      )}

      {/* Loading indicator for pagination */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-sm text-gray-600">Loading more products...</span>
        </div>
      )}

      {/* Invisible element to trigger loading */}
      {hasMore && !isFetchingNextPage && (
        <div 
          ref={lastProductRef} 
          className="h-4 w-full" 
          aria-hidden="true"
        />
      )}

      {/* End of list indicator */}
      {!hasMore && products.length > 0 && (
        <div className="text-center py-6 text-gray-500 text-sm border-t">
          <div>All products loaded ({products.length} total)</div>
        </div>
      )}
    </div>
  );
};

// Memoize the component for better performance
export const VirtualProductList = memo(VirtualProductListComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.products.length === nextProps.products.length &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.hasMore === nextProps.hasMore &&
    prevProps.isFetchingNextPage === nextProps.isFetchingNextPage &&
    // Don't compare the products array contents deeply - just the length
    // This is safe because products are immutable
    prevProps.products === nextProps.products
  );
});